/*
 * AIRBALL THEME BEHAVIOUR.
 *
 * Five custom elements and one form interception. No framework, no library:
 * everything here is a browser API, which is what the theme standards ask for
 * and what keeps the shop loading in one round trip.
 *
 * Progressive enhancement is the rule, not the aspiration. Every form on the
 * page posts to Shopify on its own and every page renders complete without
 * this file. What follows only stops the navigation and does it in place.
 */

const routes = window.Shopify?.routes?.root ?? '/';

/** Shopify returns money in cents, and Intl does the rest. */
function money(cents, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || 'de', {
    style: 'currency',
    currency: currency || window.Shopify?.currency?.active || 'EUR',
  }).format(cents / 100);
}

/* -------------------------------------------------------------------------
 * The gallery
 * ---------------------------------------------------------------------- */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.images = [...this.querySelectorAll('.gallery__image')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-go]')];
    this.counter = this.querySelector('[data-gallery-current]');
    this.status = this.querySelector('[data-gallery-status]');
    this.index = 0;
    if (this.images.length < 2) return;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryGo)));
    });

    // Arrow keys, but only while the gallery has focus, so they still scroll
    // the page everywhere else.
    const frame = this.querySelector('.gallery__frame');
    frame?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(this.index - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(this.index + 1);
      }
    });

    /*
     * Swipe. Tracked on the element rather than through a library: one
     * pointer, one threshold. The 40px floor keeps a tap from counting as a
     * swipe, and a mostly-vertical drag is left alone so the page can scroll.
     */
    let start = null;
    frame?.addEventListener('pointerdown', (event) => {
      start = { x: event.clientX, y: event.clientY };
    });
    frame?.addEventListener('pointerup', (event) => {
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      start = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.go(this.index + (dx < 0 ? 1 : -1));
    });
  }

  go(next) {
    const total = this.images.length;
    this.index = ((next % total) + total) % total;

    this.images.forEach((image, i) => {
      image.classList.toggle('is-current', i === this.index);
      image.toggleAttribute('aria-hidden', i !== this.index);
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-current', i === this.index);
      if (i === this.index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    const shown = String(this.index + 1).padStart(2, '0');
    if (this.counter) this.counter.textContent = shown;
    if (this.status) this.status.textContent = this.thumbs[this.index]?.getAttribute('aria-label') ?? '';
  }
}

/* -------------------------------------------------------------------------
 * The quantity stepper
 * ---------------------------------------------------------------------- */

class QuantityStepper extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(button.dataset.step);
        this.set(Number(this.input.value) + step);
      });
    });

    // Typing straight into the field has to obey the same bounds as the
    // buttons, otherwise the cart quietly clamps a number the page showed.
    this.input.addEventListener('change', () => this.set(Number(this.input.value)));
    this.sync();
  }

  set(value) {
    const min = Number(this.input.min) || 1;
    const max = Number(this.input.max) || 10;
    this.input.value = String(Math.min(Math.max(Math.round(value) || min, min), max));
    this.sync();
  }

  sync() {
    const value = Number(this.input.value);
    const min = Number(this.input.min) || 1;
    const max = Number(this.input.max) || 10;
    this.querySelector('[data-step="-1"]')?.toggleAttribute('disabled', value <= min);
    this.querySelector('[data-step="1"]')?.toggleAttribute('disabled', value >= max);
  }
}

/* -------------------------------------------------------------------------
 * The cart drawer
 * ---------------------------------------------------------------------- */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
      if (event.target.closest('[data-cart-scrim]')) this.close();

      const remove = event.target.closest('[data-cart-remove]');
      if (remove) this.update(remove.dataset.cartRemove, 0);

      const step = event.target.closest('[data-cart-step]');
      if (step) this.update(step.dataset.line, Number(step.dataset.cartStep));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });

    document.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.addEventListener('click', () => this.open());
    });

    /*
     * Back closes the drawer instead of leaving the shop. People treat a
     * full-height panel as a page and reach for Back first. One history entry
     * per opening, popped on close, so the entry never outlives the panel and
     * swallows the next Back press.
     */
    window.addEventListener('popstate', () => {
      this.pushed = false;
      if (this.hasAttribute('open')) this.hide();
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    if (!this.pushed) {
      history.pushState({ airballCart: true }, '');
      this.pushed = true;
    }
    this.querySelector('[data-cart-close]')?.focus();
  }

  close() {
    if (this.pushed) history.back();
    else this.hide();
  }

  hide() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  /**
   * Re-renders from the section rendering API so markup lives in Liquid.
   *
   * Throws rather than giving up quietly. Returning without replacing anything
   * leaves the panel showing what it showed before, so a cart that did receive
   * the item slides open still reading empty, which is worse than an error: it
   * tells the buyer their click did nothing. The caller catches this and sends
   * them to the cart page instead.
   */
  async refresh() {
    const response = await fetch(`${routes}?sections=cart-drawer`);
    if (!response.ok) throw new Error(`cart-drawer section: ${response.status}`);

    const data = await response.json();
    const markup = data['cart-drawer'];
    if (!markup) throw new Error('cart-drawer section came back empty');

    const fresh = new DOMParser().parseFromString(markup, 'text/html').querySelector('cart-drawer');
    if (!fresh) throw new Error('cart-drawer section held no cart-drawer element');

    this.innerHTML = fresh.innerHTML;
  }

  async update(line, quantity) {
    try {
      const response = await fetch(`${routes}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity }),
      });
      if (!response.ok) throw new Error(String(response.status));

      const cart = await response.json();
      syncCount(cart.item_count);
      await this.refresh();
    } catch {
      // The change may well have gone through, so this cannot re-send it. The
      // panel would otherwise keep showing a quantity the cart no longer has,
      // which is the one thing a cart must never do.
      window.location.assign(`${routes}cart`);
    }
  }
}

/*
 * Take a discount code back off.
 *
 * Shopify gives a theme a route to apply a code and none to drop one, so the
 * only lever is the cookie the code is held in. Which path and domain it was
 * written under is not visible from here, and a cookie is only overwritten by
 * a write matching both, so every plausible pair is cleared rather than
 * guessed at.
 *
 * Whether that worked is not assumed. The cart is re-read afterwards, and if
 * the discount survived, the buyer is told where it can be removed instead of
 * being left clicking an X that does nothing.
 */
function forgetDiscountCookie() {
  const host = window.location.hostname;
  const paths = new Set(['/', routes]);
  const domains = ['', host, `.${host}`];

  for (const path of paths) {
    for (const domain of domains) {
      const scope = domain ? `; domain=${domain}` : '';
      document.cookie = `discount_code=; Max-Age=0; path=${path}${scope}`;
    }
  }
}

document.addEventListener('click', async (event) => {
  const drop = event.target.closest('[data-discount-remove]');
  if (!drop) return;

  drop.setAttribute('disabled', '');
  forgetDiscountCookie();

  try {
    if (document.body.classList.contains('template-cart')) {
      window.location.reload();
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    await drawer?.refresh();
    const cart = await (await fetch(`${routes}cart.js`)).json();
    syncCount(cart.item_count);

    if (cart.total_discount) {
      drawer?.querySelector('[data-discount-stuck]')?.removeAttribute('hidden');
    }
  } catch {
    window.location.assign(`${routes}cart`);
  } finally {
    drop.removeAttribute('disabled');
  }
});

/*
 * Hand a discount code to Shopify.
 *
 * /discount/CODE is the only route a theme has: it puts the code on the
 * session and the checkout applies it. Nothing here can tell a good code from
 * a typo, and nothing here recomputes a total, so after handing it over the
 * cart is simply re-read. An automatic discount shows up as a real deduction;
 * a typed code does not, and the note says where it lands instead.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-cart-discount]');
  if (!form) return;

  event.preventDefault();
  const field = form.querySelector('input[name="code"]');
  const code = field?.value.trim();
  if (!code) return;

  const button = form.querySelector('[type="submit"]');
  button?.setAttribute('disabled', '');

  try {
    await fetch(`${routes}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });

    if (document.body.classList.contains('template-cart')) {
      window.location.reload();
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    await drawer?.refresh();
    const cart = await (await fetch(`${routes}cart.js`)).json();
    syncCount(cart.item_count);

    // Nothing came off the cart, so say where it will come off instead of
    // leaving a field that looks like it did nothing.
    if (!cart.total_discount) {
      drawer?.querySelector('[data-discount-note]')?.removeAttribute('hidden');
    }
  } catch {
    window.location.assign(`${routes}cart`);
  } finally {
    button?.removeAttribute('disabled');
  }
});

/*
 * Swap one bundle for the one above it.
 *
 * A single request, because a swap done as "remove, then add" has a state in
 * between where the cart holds neither, and a failure lands there. cart/update
 * takes both changes at once: the old line to zero, the new variant to the
 * quantity that line had. It adds a variant the cart does not hold yet, which
 * is exactly what this needs.
 *
 * Delegated from the document so it works in the drawer and on the cart page,
 * and keeps working after the drawer re-renders itself.
 */
document.addEventListener('click', async (event) => {
  const go = event.target.closest('[data-cart-upgrade]');
  if (!go) return;

  go.setAttribute('disabled', '');
  try {
    const response = await fetch(`${routes}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates: {
          [go.dataset.from]: 0,
          [go.dataset.to]: Number(go.dataset.quantity) || 1,
        },
      }),
    });
    if (!response.ok) throw new Error(String(response.status));

    const cart = await response.json();
    syncCount(cart.item_count);

    /*
     * On the cart page the lines are the page, not the drawer, so re-rendering
     * the drawer would leave the buyer looking at the bundle they just traded
     * away. Reload there; refresh the drawer everywhere else.
     */
    if (document.body.classList.contains('template-cart')) {
      window.location.reload();
      return;
    }
    await document.querySelector('cart-drawer')?.refresh();
  } catch {
    // The swap may have gone through in part. Re-sending it could double the
    // upgrade, so the cart page is asked instead, where whatever actually
    // happened is on screen.
    window.location.assign(`${routes}cart`);
  } finally {
    go.removeAttribute('disabled');
  }
});

/**
 * Puts a message under the buy button, or clears it when passed nothing.
 *
 * role="alert" so a screen reader announces it: the visual change is below the
 * button that was just pressed, which is exactly where a sighted buyer is
 * looking and exactly where a blind one is not.
 */
function showFormError(form, message) {
  let node = form.querySelector('.form-error');
  if (!message) {
    node?.remove();
    return;
  }
  if (!node) {
    node = document.createElement('p');
    node.className = 'form-error';
    node.setAttribute('role', 'alert');
    form.append(node);
  }
  node.textContent = message;
}

/** The badge lives in the header, outside the drawer, so it is updated here. */
function syncCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((node) => {
    node.textContent = String(count);
    node.closest('[data-cart-open]')?.setAttribute('data-count', String(count));
  });
}

/*
 * Add to cart without leaving the page.
 *
 * Delegated from the document so it covers the cards on the index and the
 * product page with one listener, and so a form rendered later by the drawer
 * refresh is covered too.
 *
 * Matched on the action as well as the marker attribute. The marker is set
 * through the Liquid form tag, and a form tag that drops the attribute would
 * silently turn every button on the shop back into a full page post. The
 * action is rendered by Shopify itself and cannot go missing, so it is the
 * one that does not depend on us being right.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-cart-add], form[action$="/cart/add"]');
  if (!form) return;

  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  button?.setAttribute('disabled', '');
  showFormError(form, null);

  let added = false;
  try {
    const response = await fetch(`${routes}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });

    if (!response.ok) {
      /*
       * A refusal is an answer, not a crash. Shopify says why in the body:
       * sold out, more asked for than exists, a bundle whose components are
       * short. Reposting the form on top of that only trades a silent button
       * for a page that is equally silent, so the reason is put on screen and
       * the buyer is left where they were.
       */
      const reason = await response.json().catch(() => null);
      if (reason) {
        showFormError(form, reason.description || reason.message);
        return;
      }
      throw new Error(String(response.status));
    }
    added = true;

    const cart = await (await fetch(`${routes}cart.js`)).json();

    /*
     * Shopify accepted the add and the cart is still empty. That happens when
     * the cart cookie never survives the round trip: a browser refusing to
     * store it, or the shop being viewed inside someone else's frame, where
     * the cookie is third-party and gets dropped. Every request then starts a
     * fresh, empty cart.
     *
     * Opening the panel here would show "nothing in it" one moment after the
     * buyer was told it worked, and they would reasonably conclude the shop is
     * broken. Say what happened instead.
     */
    if (cart.item_count === 0) {
      showFormError(form, form.dataset.cartBlocked || null);
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    await drawer?.refresh();
    syncCount(cart.item_count);
    drawer?.open();
  } catch {
    /*
     * Two different failures, and they must not share a recovery.
     *
     * If the add itself failed, the plain form post is the fallback: it is
     * what happens without JavaScript at all, and it ends on the cart page
     * rather than in silence.
     *
     * If the add succeeded and it was the drawer that broke, posting the form
     * again would put a second one in the cart. The item is already there, so
     * the only thing left is to go and show it.
     */
    if (added) window.location.assign(`${routes}cart`);
    else form.submit();
  } finally {
    button?.removeAttribute('disabled');
  }
});

/* -------------------------------------------------------------------------
 * The community popup
 * ---------------------------------------------------------------------- */

/*
 * Asks once, then leaves people alone. Somebody who closes it is not asked
 * again for two weeks; somebody who signs up is never asked again. Both are
 * remembered in the browser, because that is the only memory there is for a
 * visitor who is not logged in. If storage is blocked the popup simply asks
 * on every visit, which is the right way round to fail: annoying, not broken.
 *
 * The native dialog does the hard parts: focus is trapped while it is open,
 * Escape closes it, and focus goes back where it was when it shuts.
 */
const POPUP_KEY = 'airball:community';
const POPUP_QUIET_MS = 14 * 24 * 60 * 60 * 1000;
const POPUP_VISIT_KEY = 'airball:visit-start';

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (typeof this.dialog?.showModal !== 'function') return;

    this.translate();

    this.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });

    // A click on the dimmed page lands on the dialog element itself, never on
    // anything inside it, so that is the test for "clicked outside".
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.dialog.addEventListener('close', () => {
      if (this.read() !== 'joined') this.write(String(Date.now()));
    });

    // Disabled rather than removed: the button stays where the thumb is and
    // a second tap while the page is leaving cannot post the form twice.
    this.querySelector('form')?.addEventListener('submit', (event) => {
      event.submitter?.setAttribute('disabled', '');
    });

    /*
     * Copy the code. The clipboard API needs a secure page and a permission
     * the browser may refuse; when it does, the code is selected instead so a
     * long-press or Ctrl+C still takes it. The label says what happened.
     */
    this.querySelector('[data-popup-copy]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const value = this.querySelector('[data-popup-code]');
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value.textContent.trim());
        const label = button.textContent;
        button.textContent = button.dataset.copied;
        window.setTimeout(() => (button.textContent = label), 2000);
      } catch {
        window.getSelection()?.selectAllChildren(value);
      }
    });

    /*
     * Back from the post. Shopify reloads the page with the form's result
     * rendered into it, so the popup opens straight away to say how it went,
     * and the marker Shopify adds to the address is tidied out of it so a
     * reload or a shared link does not replay the message.
     */
    const result = this.querySelector('[data-popup-state]')?.dataset.popupState;
    if (result) {
      if (result === 'success') this.write('joined');
      this.tidyAddress();
      this.show();
      return;
    }

    const preview = new URLSearchParams(window.location.search).get('popup') === 'preview';
    if (!preview && !this.shouldAsk()) return;

    const delay = preview ? 300 : this.remaining(Number(this.dataset.delay || 20) * 1000);
    this.timer = window.setTimeout(() => this.show(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }

  /*
   * The delay counts time on the shop, not time on this page. Someone who
   * opens a product after fifteen seconds has been here fifteen seconds, and
   * starting the count again on every click would mean an active visitor is
   * the one who never sees it. The visit's start is kept for the tab's life.
   * Never less than a second, so it does not land on top of a page that is
   * still painting.
   */
  remaining(total) {
    let start = Date.now();
    try {
      const saved = Number(window.sessionStorage.getItem(POPUP_VISIT_KEY));
      if (saved) start = saved;
      else window.sessionStorage.setItem(POPUP_VISIT_KEY, String(start));
    } catch {
      // No storage: every page counts from zero, which is what it did before.
    }
    return Math.max(1000, total - (Date.now() - start));
  }

  /*
   * Spanish or Portuguese, when the browser asks for it. The visitor's
   * language list is walked in order and the first language the popup can
   * speak wins: German or English means the page is already right as served,
   * Spanish or Portuguese means swapping the words from the dictionary in the
   * section. ?popup_lang=es forces it, for a look without changing a phone's
   * settings. The language tag on the sign-up follows, so the list knows
   * which language these people read.
   */
  translate() {
    let dictionary;
    try {
      dictionary = JSON.parse(this.querySelector('[data-popup-i18n]')?.textContent || '{}');
    } catch {
      return;
    }

    const forced = new URLSearchParams(window.location.search).get('popup_lang');
    const wanted = forced ? [forced] : navigator.languages?.length ? navigator.languages : [navigator.language];
    let lang = null;
    for (const tag of wanted) {
      const base = String(tag || '').toLowerCase().split('-')[0];
      if (dictionary[base]) {
        lang = base;
        break;
      }
      if (base === 'de' || base === 'en') return;
    }
    if (!lang) return;

    const words = dictionary[lang];
    const fill = (text) =>
      text.replaceAll('{percent}', this.dataset.percent || '5').replaceAll('{privacy_url}', this.dataset.privacyUrl || '');

    this.querySelectorAll('[data-i18n]').forEach((node) => {
      const text = words[node.dataset.i18n];
      if (!text) return;
      // Markup only ever comes from the dictionary above, never from input.
      if (node.dataset.i18n.endsWith('_html')) node.innerHTML = fill(text);
      else node.textContent = fill(text);
    });
    this.querySelectorAll('[data-i18n-placeholder]').forEach((node) => {
      const text = words[node.dataset.i18nPlaceholder];
      if (text) node.placeholder = fill(text);
    });
    this.querySelectorAll('[data-i18n-aria]').forEach((node) => {
      const text = words[node.dataset.i18nAria];
      if (text) node.setAttribute('aria-label', fill(text));
    });

    const copy = this.querySelector('[data-popup-copy]');
    if (copy && words.copied) copy.dataset.copied = words.copied;

    this.dialog.lang = lang;
    const tags = this.querySelector('[data-popup-tags]');
    if (tags) tags.value = tags.value.replace(/lang-[\w-]+/, `lang-${lang}`);
  }

  shouldAsk() {
    if (this.dataset.enabled !== 'true') return false;
    if (this.dataset.subscribed === 'true') return false;
    // The cart page is where someone is about to pay. Not the moment.
    if (document.body.classList.contains('template-cart')) return false;

    const saved = this.read();
    if (saved === 'joined') return false;
    if (saved && Date.now() - Number(saved) < POPUP_QUIET_MS) return false;
    return true;
  }

  show() {
    if (this.dialog.open) return;
    // Never on top of the cart. Someone looking at their basket is doing the
    // thing the shop is for; the popup waits until the drawer is shut.
    if (document.querySelector('cart-drawer[open]')) {
      this.timer = window.setTimeout(() => this.show(), 5000);
      return;
    }
    this.dialog.showModal();
    // The window, not the close cross, so nothing opens already lit up.
    this.dialog.focus();
  }

  tidyAddress() {
    const url = new URL(window.location.href);
    url.searchParams.delete('customer_posted');
    url.hash = '';
    window.history.replaceState(window.history.state, '', url);
  }

  read() {
    try {
      return window.localStorage.getItem(POPUP_KEY);
    } catch {
      return null;
    }
  }

  write(value) {
    try {
      window.localStorage.setItem(POPUP_KEY, value);
    } catch {
      // Private mode or blocked storage. See above: it asks again next time.
    }
  }
}

customElements.define('product-gallery', ProductGallery);
customElements.define('quantity-stepper', QuantityStepper);
customElements.define('cart-drawer', CartDrawer);
customElements.define('newsletter-popup', NewsletterPopup);

export { money };
